import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Info, AlertTriangle, X } from 'lucide-react';

export interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'info' | 'warning';
  onClose?: () => void;
  duration?: number;
  index?: number;
}

const MAX_VISIBLE_TOASTS = 3;

const iconMap: Record<string, React.FC<{ className?: string }>> = {
  success: CheckCircle,
  error: XCircle,
  info: Info,
  warning: AlertTriangle,
};

const bgColorMap: Record<string, string> = {
  success: 'bg-green-500',
  error: 'bg-red-500',
  info: 'bg-blue-500',
  warning: 'bg-amber-500',
};

export const Toast: React.FC<ToastProps> = ({ 
  message, 
  type = 'info', 
  onClose, 
  duration = 3000,
  index = 0,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(100);
  const Icon = iconMap[type] || Info;

  useEffect(() => {
    const startTime = Date.now();
    const totalDuration = duration;

    // Smooth progress bar animation
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, ((totalDuration - elapsed) / totalDuration) * 100);
      setProgress(remaining);
    }, 50);

    const timer = setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, duration);

    return () => {
      clearTimeout(timer);
      clearInterval(progressInterval);
    };
  }, [duration, onClose]);

  if (!isVisible) return null;

  const bottomOffset = 16 + index * 64;

  return (
    <div 
      className="fixed z-50 animate-slide-up"
      style={{ bottom: `${bottomOffset}px`, right: '16px' }}
    >
      <div className={`${bgColorMap[type] || bgColorMap.info} text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 relative overflow-hidden min-w-[280px] max-w-[420px]`}>
        <Icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
        <span className="text-sm font-medium flex-1">{message}</span>
        <button
          onClick={() => {
            setIsVisible(false);
            onClose?.();
          }}
          className="text-white/80 hover:text-white min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label="Dismiss notification"
        >
          <X className="w-4 h-4" />
        </button>
        <div 
          className="absolute bottom-0 left-0 h-1 bg-white/30"
          style={{ width: `${progress}%`, transition: 'width 100ms linear' }}
        />
      </div>
    </div>
  );
};

// Toast hook for managing toasts
let toastCallback: ((message: string, type: 'success' | 'error' | 'info' | 'warning') => void) | null = null;

export const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
  if (toastCallback) {
    toastCallback(message, type);
  }
};

export const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<Array<{ id: number; message: string; type: 'success' | 'error' | 'info' | 'warning' }>>([]);
  const [idCounter, setIdCounter] = useState(0);

  useEffect(() => {
    toastCallback = (message, type) => {
      const newId = idCounter + 1;
      setIdCounter(newId);
      setToasts(prev => {
        // M5: Limit to MAX_VISIBLE_TOASTS visible toasts, discard oldest
        const updated = [...prev, { id: newId, message, type }];
        if (updated.length > MAX_VISIBLE_TOASTS) {
          return updated.slice(updated.length - MAX_VISIBLE_TOASTS);
        }
        return updated;
      });
    };

    return () => {
      toastCallback = null;
    };
  }, [idCounter]);

  const removeToast = (id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  return (
    <div role="status" aria-live="polite" aria-atomic="true">
      {toasts.map((toast, index) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
          index={index}
        />
      ))}
    </div>
  );
};

export default Toast;
